import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Headphones, Plus, Shield, ExternalLink, Sparkles, Lock, Check, Loader2 } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import PricingModal from "../components/PricingModal";

const SAMPLE_PROGRAMS = [
  { name: "Joe Rogan Experience", lane: "Independent", connected: false, sample_trust: 58 },
  { name: "All-In Podcast", lane: "Independent", connected: false, sample_trust: 72 },
  { name: "The Megyn Kelly Show", lane: "Conservative", connected: false, sample_trust: 78 },
  { name: "Pivot with Kara Swisher", lane: "Center-Left", connected: false, sample_trust: 70 },
  { name: "The Daily (NYT)", lane: "Center-Left", connected: true, sample_trust: 82 },
  { name: "Honestly with Bari Weiss", lane: "Center", connected: true, sample_trust: 85 },
];

export default function Connect() {
  const { user } = useAuth();
  const [showPricing, setShowPricing] = useState(false);
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const tier = user?.tier || "free";
  const isProOrBetter = ["pro", "channels", "elite", "business"].includes(tier);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) return;
    if (!user) { setShowPricing(true); return; }
    if (!isProOrBetter) { setShowPricing(true); return; }
    setSubmitting(true);
    try {
      // Best-effort POST — endpoint may be a stub today; UI confirms intent
      await http.post("/connect/programs", { url: url.trim() }).catch(() => null);
      setSubmitted(true);
      setUrl("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">

        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] via-[#1e3a8a] to-[#1e40af] text-white p-7 sm:p-9 mb-8 relative overflow-hidden" data-testid="connect-hero">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[var(--gold)] opacity-10 blur-3xl rounded-full" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)] mb-2 flex items-center gap-2">
              <Sparkles size={12} /> Newseze Connect
            </div>
            <h1 className="text-[28px] sm:text-[40px] font-black tracking-[-0.02em] leading-[1.05] max-w-[760px]">
              Bring your own podcasts &amp; news programs. Get Newseze trust scoring on every claim.
            </h1>
            <p className="text-[14px] sm:text-[15px] opacity-85 mt-3 max-w-[700px] leading-snug">
              Paste an RSS feed, podcast URL, or YouTube channel — Newseze ingests new episodes, transcribes them,
              and applies the same 5-axis trust scoring (evidence · balance · impact · respect · drama) we use on
              everything else. Your personal feed, fact-checked.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[12px] font-bold">Pro tier &amp; up</span>
              <span className="bg-[var(--gold)] text-[#1d1303] rounded-full px-3 py-1 text-[12px] font-black">Customize your news</span>
            </div>
          </div>
        </div>

        {/* Submit form */}
        <div className="rounded-xl border border-[var(--line)] bg-white p-5 mb-8" data-testid="connect-form">
          <div className="text-[12px] uppercase tracking-[0.16em] font-black text-[var(--blue)] mb-3 flex items-center gap-2">
            <Plus size={14} /> Connect a podcast or program
          </div>
          <form className="flex flex-col sm:flex-row gap-2" onSubmit={onSubmit}>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://feeds.example.com/your-podcast.xml — or paste an Apple/Spotify/YouTube link"
              className="flex-1 border border-[var(--line)] rounded-md px-3 py-2.5 text-[13.5px] focus:outline-none focus:border-[var(--blue)]"
              data-testid="connect-url-input"
              required
            />
            <button
              type="submit"
              disabled={submitting || !url.trim()}
              className={`btn ${isProOrBetter ? "primary" : "gold"} justify-center`}
              data-testid="connect-submit-btn"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Connecting…</>
                : isProOrBetter ? <>Connect &amp; verify →</>
                : <><Lock size={13} /> Upgrade to Pro to connect</>}
            </button>
          </form>
          {submitted && (
            <div className="mt-3 text-[12.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="connect-success">
              <Check size={14} /> Got it. Newseze will start ingesting episodes within an hour. You&apos;ll see them in your personal feed.
            </div>
          )}
        </div>

        {/* What you get */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8" data-testid="connect-benefits">
          {[
            { icon: Shield, title: "Trust scoring", body: "Every claim from your connected programs runs through the same 5-axis trust model: evidence, balance, impact, respect, drama." },
            { icon: Sparkles, title: "AI verification", body: "When a host makes a specific factual claim, Newseze cross-checks it against the wire and flags it if the claim contradicts evidence." },
            { icon: Headphones, title: "Your personal feed", body: "Connected programs appear in your For You row alongside Newseze stories — trust-ranked alongside the wire." },
          ].map((b, i) => (
            <div key={i} className="rounded-xl border border-[var(--line)] bg-white p-4">
              <b.icon size={18} className="text-[var(--gold)] mb-2" />
              <div className="text-[14px] font-black text-[var(--ink)] mb-1">{b.title}</div>
              <p className="text-[12.5px] text-[var(--muted)] leading-snug">{b.body}</p>
            </div>
          ))}
        </div>

        {/* Sample programs */}
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--blue)] mb-3">Popular programs being connected</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {SAMPLE_PROGRAMS.map((p) => (
              <div key={p.name} className="border border-[var(--line)] bg-white rounded-md p-3 flex items-center gap-3" data-testid={`connect-sample-${p.name.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="w-10 h-10 rounded-md bg-[var(--sky)] grid place-items-center text-[var(--blue)]">
                  <Headphones size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-black text-[var(--ink)] truncate">{p.name}</div>
                  <div className="text-[10px] text-[var(--muted)] uppercase tracking-[0.12em] font-bold">{p.lane}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--muted)]">Trust</div>
                  <div className="text-[14px] font-black text-[var(--blue)]">{p.sample_trust}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Premium customization tier teaser */}
        <div className="rounded-xl border border-[var(--gold)] bg-gradient-to-br from-[#fff7d6] to-white p-5" data-testid="connect-upgrade-cta">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[#7a5b00] mb-1">Premium customization</div>
              <div className="text-[16px] font-black text-[var(--ink)]">Newseze Connect is included with Pro ($9.99) and above.</div>
              <p className="text-[12.5px] text-[var(--muted)] mt-1">Channel Builder ($19.99) and Elite ($49.99) unlock your own branded channels — turn connected programs into a public feed for your audience.</p>
            </div>
            <button
              className="btn gold"
              onClick={() => setShowPricing(true)}
              data-testid="connect-see-plans-btn"
            >
              See plans →
            </button>
          </div>
        </div>

      </main>
      <Footer />
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} />}
    </>
  );
}
